import React from "react";
import {
    Box,
    Checkbox,
    List,
    ListItem,
    ListItemButton,
    Stack
} from "@mui/material";
import styles from '../DynamicDataCardList.module.scss';
import { calcProPercentsBasedOnFilterAndDays, itemPercentage, percentageLabel } from "../../../../../utilis/common";
import { calculateDiffBetweenPercentages, pickComparisonColor } from "../../../../../utilis/percentage-comparison";
import { useSelector } from "react-redux";
import useLoadingToggle from "../../../../../components/hooks/useLoadingToggle";
import NoRecordFound from "../../../../../components/shared/NoRecordFound";
import CheckboxLoader from "../../../../../components/shared/checkbox-loader/CheckboxLoader";
import CardItemTooltip from "../../../../../components/shared/CardItemTooltip";
import CheckboxCircle from "../../../../../components/dashboard/shared/checkboxButton/CheckboxCircle";
import ColorBox from "../../../../../components/shared/dashboard/ColorBox";
import useMergedData from "../../../../../components/hooks/useMergedData";
import { PAGE_TYPE } from "../../../../../types/pages.type";


const DxCardTemplate = ({
    data,
    dataComparison,
    handleToggle,
    selectedItem = [],
    type,
    page,
    filter,
    filterComparison,
    averageCensusComparison,
    averageCensus,
    cardTitle,
    loading,
    projectionDays,
    priorityNumber,
    transferType,
    isComparingAgainstAvgCensus,
    lockedTotalBy,
    searchValue,
    question,
    spacialSelectedItem = [],
    isCustomTab = false,
    customTab = null,
    disableInteractions = false,
    admissionCompareAgainst = null,
    admissionCompareAgainstComparison = null,
	reverseColorsAdmissionPage = false,
	compactForHeaderPopover = false,
	isMobile = false,
}) => {
    const specialPages = [PAGE_TYPE.ADMISSION];
    const isSpecialPage = specialPages.find((x) => x === page)?.length > 0;
    const isFirstItemInPriorityData = priorityNumber === 1;

    const { comparingAgainstDifferentFacility } = useSelector(state => state.comparison);

    const { loadingItems, handleToggleWithLoader } = useLoadingToggle();
    const mergedData = useMergedData(data, dataComparison);

    const containerRef = React.useRef(null);
    React.useEffect(() => {
        if (containerRef.current && containerRef.current.parentElement) {
            containerRef.current.parentElement.style.position = 'relative';
        }
    }, []);

    return (
        <div 
            ref={containerRef}
            className={`${styles.diagnosticsCardList} `} 
            style={
                compactForHeaderPopover || isMobile
                    ? { position: "relative", height: "auto", minHeight: 0, width: "100%" }
                    : { position: "absolute", top: 0, left: 0, height: "100%", width: "100%" }
            }
        >
            <List
                sx={{
                    width: "100%",
                    bgcolor: "background.paper",
                }}
                dense={true}
                disablePadding={true}
            >
                {mergedData.length > 0 &&
                    mergedData
                        .filter((x) => x.label.toLowerCase().includes((searchValue || "").toLowerCase()))
                        .map((item, index) => {
                            const ID = item._id;
                            const isSpacialItem = item?.isSpacialItem ?? false;
                            const labelId = `checkbox-list-label-${ID}`;
                            const selected = selectedItem.indexOf(ID) !== -1;
                            const itemComparison = dataComparison?.find((x) => x._id === item._id);
                            let comparisonColor;
                            let comparingAgainst;
                            let comparingAgainstScaled;
                            let numberOfDays;

                            if (page === PAGE_TYPE.ADMISSION) {
                                const percentage = itemPercentage(item.total, admissionCompareAgainst, "number", page);

                                const percentageComparison = itemPercentage(
                                    itemComparison?.total,
                                    admissionCompareAgainstComparison,
                                    "number",
                                    page
                                );

                                const {
                                    percentageDiff,
                                    itemTotalComparisonScaled,
                                    numberOfDays: numberOfDaysTemp,
                                } = calculateDiffBetweenPercentages(
                                    percentage,
                                    percentageComparison,
                                    reverseColorsAdmissionPage,
                                    filter,
                                    filterComparison,
                                    itemComparison?.total,
                                    projectionDays
                                );

                                numberOfDays = numberOfDaysTemp;
                                comparingAgainstScaled = itemTotalComparisonScaled;
                                comparisonColor = pickComparisonColor(percentageDiff, isSpecialPage);
                                comparingAgainst = itemComparison?.total;
                            } else {
                                const percentage = itemPercentage(item.total, averageCensus, "number", page);

                                const percentageComparison = itemPercentage(
                                    itemComparison?.total || 0,
                                    averageCensusComparison,
                                    "number",
                                    page
                                );
                                const {
                                    percentageDiff,
                                    itemTotalComparisonScaled,
                                    numberOfDays: numberOfDaysTemp,
                                } = calculateDiffBetweenPercentages(
                                    percentage,
                                    percentageComparison,
                                    false,
                                    filter,
                                    filterComparison,
                                    itemComparison?.total,
                                    projectionDays
                                );

                                numberOfDays = numberOfDaysTemp;
                                comparingAgainstScaled = itemTotalComparisonScaled;
                                comparisonColor = pickComparisonColor(percentageDiff, isSpecialPage);
                                comparingAgainst = itemComparison?.total;
                            }

                            if (comparingAgainstDifferentFacility) {
                                comparisonColor = null;
                            }

                            return (
                                <ListItem
                                    sx={{ padding: "5px" }}
                                    key={ID}
                                    dense={true}
                                    disablePadding={true}
                                    className={`${styles.listItem} ${selected ? styles.selected : null}`}
                                >
                                    <ListItemButton
                                        sx={{
                                            borderRadius: "8px",
                                            paddingLeft: 2,
                                            "@media screen and (max-width: 1424px)": {
                                                paddingLeft: 0.8,
                                            },
                                            paddingRight: { md: 1, lg: 2 },
                                        }}
                                        role={undefined}
                                        onClick={() => !isSpacialItem && !disableInteractions ? handleToggleWithLoader(() => handleToggle({
                                            item,
                                            type,
                                            itemComparison: itemComparison,
                                            isChecked: selected,
                                            cardTitle,
                                            question: { isCustom: true, customQuestionInputType: question?.customQuestionInputType, isCustomTab, cardTitle, customTab },
                                            isCustomTab
                                        }), item?._id) : undefined}
                                        dense
                                        className={`${styles.listItemButton}`}
                                    >
                                        <Stack direction={"row"} sx={{ width: "100%" }}>
                                            <Stack
                                                sx={{ width: "100%" }}
                                                direction="row"
                                                alignItems={"center"}
                                                justifyContent="space-between"
                                            >
                                                <div className={`${styles.title}`} style={{ display: "flex", alignItems: "center" }}>
                                                    <ColorBox
                                                        sx={{ mr: "10px" }}
                                                        color={comparisonColor}
                                                        comparingAgainst={comparingAgainst}
                                                        comparingAgainstScaled={comparingAgainstScaled}
                                                        numberOfDays={numberOfDays}
                                                    />
                                                    <Box position="relative" display="inline-flex" alignItems="center">
                                                        {isSpacialItem ? (
                                                            <>
                                                                <CheckboxCircle
                                                                    disabled={item?.isReadOnly || disableInteractions}
                                                                    className={styles.checkboxButton}
                                                                    labelId={labelId}
                                                                    handleToggle={() =>
                                                                        handleToggleWithLoader(() => handleToggle({
                                                                            item: item,
                                                                            type,
                                                                            itemComparison: itemComparison,
                                                                            isChecked: spacialSelectedItem?.indexOf(ID) !== -1,
                                                                            cardTitle,
                                                                            isSpacialItem: item?.isSpacialItem ?? false,
                                                                            isCustomTab
                                                                        }), ID)
                                                                    }
                                                                    sx={{ ...(loadingItems[ID] && { opacity: 0 }) }}
                                                                    checked={spacialSelectedItem?.indexOf(ID) !== -1 || selectedItem.indexOf(ID) !== -1}
                                                                />
                                                                <CheckboxLoader
                                                                    className={"small-dots-left"}
                                                                    isLoading={loadingItems[item?._id]}
                                                                />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Checkbox
                                                                    edge="start"
                                                                    checked={selected}
                                                                    tabIndex={-1}
                                                                    disableRipple
                                                                    inputProps={{ "aria-labelledby": labelId }}
                                                                    disabled={item?.isReadOnly || disableInteractions}
                                                                    sx={{
                                                                        paddingRight: { md: 0.2, lg: 0.5 },
                                                                        ...(loadingItems[item?._id] && { opacity: 0 }),
                                                                        ...(item?.isReadOnly && {
                                                                            '&.MuiCheckbox-root.Mui-disabled': {
                                                                                cursor: 'not-allowed !important',
                                                                                pointerEvents: 'auto !important'
                                                                            },
                                                                            '&.MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root': {
                                                                                cursor: 'not-allowed !important'
                                                                            }
                                                                        })
                                                                    }}
                                                                />
                                                                <CheckboxLoader
                                                                    className={"small-dots-left"}
                                                                    isLoading={loadingItems[item?._id]}
                                                                />
                                                            </>
                                                        )}
                                                    </Box>
                                                    <CardItemTooltip item={item}>
                                                        <span>{item.label} &nbsp;</span>
                                                    </CardItemTooltip>
                                                    <Box
                                                        component={"span"}
                                                        sx={{
                                                            display: "none",
                                                            "@media screen and (max-width: 1600px)": {
                                                                display: "inline",
                                                            },
                                                        }}
                                                    >
                                                        <span className="ffmar-bold">
                                                            {calcProPercentsBasedOnFilterAndDays(item.total, filter, projectionDays)}{" "}
                                                        </span>
                                                        <span className="ffmar-reg">

                                                            {item?.isReadOnly ? "0%" : percentageLabel(
                                                                calcProPercentsBasedOnFilterAndDays(
                                                                    item.percentage ? item.percentage : itemPercentage(item.total, null, null, page),
                                                                    filter,
                                                                    projectionDays,
                                                                    true,
                                                                    isComparingAgainstAvgCensus ||
                                                                    lockedTotalBy === "census" ||
                                                                    (isFirstItemInPriorityData && (!transferType || transferType.length === 0))
                                                                )
                                                            )}
                                                        </span>
                                                    </Box>
                                                </div>
                                                <Box
                                                    component={"span"}
                                                    sx={{
                                                        display: "inline",
                                                        "@media screen and (max-width: 1601px)": {
                                                            display: "none",
                                                        },
                                                    }}
                                                >
                                                    <div className={`${styles.totalCount}`}>
                                                        <span className="ffmar-bold">
                                                            {calcProPercentsBasedOnFilterAndDays(item.total, filter, projectionDays)}{" "}
                                                        </span>
                                                        {!isSpacialItem && (
                                                            <span className="ffmar-reg">
                                                                &nbsp;
                                                                {item?.isReadOnly ? "(0%)" : percentageLabel(
                                                                    calcProPercentsBasedOnFilterAndDays(
                                                                        item.percentage ? item.percentage : itemPercentage(item.total, null, null, page),
                                                                        filter,
                                                                        projectionDays,
                                                                        true,
                                                                        isComparingAgainstAvgCensus ||
                                                                        lockedTotalBy === "census" ||
                                                                        (isFirstItemInPriorityData && (!transferType || transferType.length === 0))
                                                                    )
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                {!loading && <NoRecordFound data={mergedData} />}
            </List>
        </div>
    );
};

export default DxCardTemplate;
